import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import {
  SkillsIdeateDataFacade,
  SkillsIdeateEvent,
  SkillsIdeateProgressTrackService,
} from '@core/tellent-skills-logic';
import type { SkillsIdeateResultJson } from '@core/tellent-skills-types';
import {
  DialogBodyComponent,
  DialogComponentI,
  DialogHeaderComponent,
  DialogRef,
  SpinnerComponent,
} from '@core/user-interface-legacy';

import { LoadingGameFrameComponent } from '../../import-skills/import-skills-loading-dialog/loading-game-frame/loading-game-frame.component';

@Component({
  selector: 'tlc-skills-ideate-loading-dialog',
  templateUrl: './skills-ideate-loading-dialog.component.html',
  styleUrl: './skills-ideate-loading-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogBodyComponent,
    DialogHeaderComponent,
    TranslatePipe,
    LoadingGameFrameComponent,
    SpinnerComponent,
  ],
})
export class SkillsIdeateLoadingDialogComponent implements DialogComponentI<
  { id: number },
  { resultJson: SkillsIdeateResultJson } | { error: string }
> {
  private readonly t = injectTranslate();
  private readonly dialogRef = inject(DialogRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly facade = inject(SkillsIdeateDataFacade);
  private readonly progressTrack = inject(SkillsIdeateProgressTrackService);

  protected readonly gameStartText = this.t(
    'tlc.positions.import_skills_flow.loading_dialog.loading_game.start',
  );
  protected readonly gameTutorialText = this.t(
    'tlc.positions.import_skills_flow.loading_dialog.loading_game.tutorial',
  );
  protected readonly gameOverText = this.t(
    'tlc.positions.import_skills_flow.loading_dialog.loading_game.game_over',
  );
  protected readonly gameScoreText = this.t(
    'tlc.positions.import_skills_flow.loading_dialog.loading_game.score',
  );

  public setDialogData({ id }: { id: number }, _triggerUpdate: boolean): void {
    this.progressTrack
      .track(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: message => {
          if (message.event === SkillsIdeateEvent.Done) {
            this.loadResult(id);
            return;
          }
          if (message.event === SkillsIdeateEvent.Failed) {
            this.dialogRef.close({ error: message.reason || this.genericError() });
          }
        },
        error: () => {
          this.dialogRef.close({ error: this.genericError() });
        },
      });
  }

  private loadResult(id: number): void {
    this.facade
      .fetchIdeate(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ideate => {
          if (ideate.status === 'failed' || !ideate.resultJson) {
            this.dialogRef.close({ error: ideate.errorMessage ?? this.genericError() });
            return;
          }
          this.dialogRef.close({ resultJson: ideate.resultJson });
        },
        error: () => {
          this.dialogRef.close({ error: this.genericError() });
        },
      });
  }

  private genericError(): string {
    return this.t('tlc.positions.skills_ideate_flow.result.failed.description');
  }
}
